import type { ReactElement } from 'react';
import { SessionPersistenceType, UrlPatternsRuleAction } from '@pillar-portal/api-types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type {
    AllowedWebsiteTemplateEditorState,
    AllowedWebsiteTemplateNamespaceMode,
} from './AllowedWebsiteTemplateEditor';

export interface AllowedWebsiteTemplateSystemSelectOption {
    readonly systemId: number;
    readonly label: string;
}

export interface AllowedWebsiteTemplateNamespaceOption {
    readonly mode: AllowedWebsiteTemplateNamespaceMode;
    readonly label: string;
}

export interface AllowedWebsiteTemplateConfigFormProps {
    readonly state: AllowedWebsiteTemplateEditorState;
    readonly onChange: (state: AllowedWebsiteTemplateEditorState) => void;
    readonly namespaceOptions: readonly AllowedWebsiteTemplateNamespaceOption[];
    readonly gatewaySystemOptions: readonly AllowedWebsiteTemplateSystemSelectOption[];
    readonly systemOfRecordSystemOptions: readonly AllowedWebsiteTemplateSystemSelectOption[];
    readonly getDefaultSystemIdTextForNamespaceMode: (
        namespaceMode: AllowedWebsiteTemplateNamespaceMode
    ) => string;
}

function RadioField(props: {
    readonly label: string;
    readonly name: string;
    readonly checked: boolean;
    readonly onChange: () => void;
}): ReactElement {
    return (
        <label className="flex items-center gap-2 text-sm">
            <input
                type="radio"
                aria-label={props.label}
                name={props.name}
                checked={props.checked}
                className="h-4 w-4 accent-blue-600"
                onChange={props.onChange}
            />
            <span>{props.label}</span>
        </label>
    );
}

function renderSystemOfRecordSystemSelector(
    props: AllowedWebsiteTemplateConfigFormProps
): ReactElement | null {
    if (props.state.namespaceMode !== 'SYSTEM_OF_RECORD') {
        return null;
    }

    return (
        <div className="space-y-2">
            <Label htmlFor="system_of_record_system_id">System of record system</Label>
            <select
                id="system_of_record_system_id"
                className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={props.state.ipxSystemIdText}
                onChange={(event) =>
                    props.onChange({
                        ...props.state,
                        ipxSystemIdText: event.target.value,
                    })
                }
            >
                {props.systemOfRecordSystemOptions.map((option) => (
                    <option key={option.systemId} value={String(option.systemId)}>
                        {option.label}
                    </option>
                ))}
            </select>
        </div>
    );
}

function renderGatewaySystemSelector(
    props: AllowedWebsiteTemplateConfigFormProps
): ReactElement | null {
    if (props.state.namespaceMode !== 'GATEWAY') {
        return null;
    }

    return (
        <div className="space-y-2">
            <Label htmlFor="gateway_system_id">Gateway system</Label>
            <select
                id="gateway_system_id"
                className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={props.state.ipxSystemIdText}
                onChange={(event) =>
                    props.onChange({
                        ...props.state,
                        ipxSystemIdText: event.target.value,
                    })
                }
            >
                {props.gatewaySystemOptions.map((option) => (
                    <option key={option.systemId} value={String(option.systemId)}>
                        {option.label}
                    </option>
                ))}
            </select>
        </div>
    );
}

export function AllowedWebsiteTemplateConfigForm(
    props: AllowedWebsiteTemplateConfigFormProps
): ReactElement {
    return (
        <div className="space-y-4">
            <Card>
                <CardHeader>
                    <CardTitle className="text-base">Template</CardTitle>
                </CardHeader>
                <CardContent className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                        <Label htmlFor="template_name">Template name</Label>
                        <Input
                            id="template_name"
                            value={props.state.templateName}
                            onChange={(event) =>
                                props.onChange({
                                    ...props.state,
                                    templateName: event.target.value,
                                })
                            }
                        />
                    </div>
                    <fieldset className="space-y-2">
                        <legend className="text-sm font-medium">Allowed website</legend>
                        <div className="flex flex-wrap gap-4">
                            {props.namespaceOptions.map((option) => (
                                <RadioField
                                    key={option.mode}
                                    label={option.label}
                                    name="allowed_website_namespace"
                                    checked={props.state.namespaceMode === option.mode}
                                    onChange={() =>
                                        props.onChange({
                                            ...props.state,
                                            namespaceMode: option.mode,
                                            ipxSystemIdText:
                                                props.getDefaultSystemIdTextForNamespaceMode(
                                                    option.mode
                                                ),
                                        })
                                    }
                                />
                            ))}
                        </div>
                        {renderGatewaySystemSelector(props)}
                        {renderSystemOfRecordSystemSelector(props)}
                    </fieldset>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle className="text-base">Website policy</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                            <Label htmlFor="website_name">Website name</Label>
                            <Input
                                id="website_name"
                                value={props.state.websiteName}
                                onChange={(event) =>
                                    props.onChange({
                                        ...props.state,
                                        websiteName: event.target.value,
                                    })
                                }
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="website_url">Website URL</Label>
                            <Input
                                id="website_url"
                                value={props.state.websiteUrl}
                                onChange={(event) =>
                                    props.onChange({
                                        ...props.state,
                                        websiteUrl: event.target.value,
                                    })
                                }
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="allowed_url_patterns">Allowed URL patterns</Label>
                        <textarea
                            id="allowed_url_patterns"
                            className="min-h-24 w-full rounded-md border px-3 py-2 text-sm"
                            value={props.state.allowedUrlPatternsText}
                            onChange={(event) =>
                                props.onChange({
                                    ...props.state,
                                    allowedUrlPatternsText: event.target.value,
                                })
                            }
                        />
                        <p className="text-sm text-muted-foreground">
                            Optional. When empty, only the Website URL is allowed.
                        </p>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                            <Label htmlFor="session_persistence">Session persistence</Label>
                            <select
                                id="session_persistence"
                                className="w-full rounded-md border px-3 py-2"
                                value={props.state.sessionPersistence}
                                onChange={(event) =>
                                    props.onChange({
                                        ...props.state,
                                        sessionPersistence:
                                            event.target.value === SessionPersistenceType.EPHEMERAL
                                                ? SessionPersistenceType.EPHEMERAL
                                                : SessionPersistenceType.PERSISTENT,
                                    })
                                }
                            >
                                <option value={SessionPersistenceType.PERSISTENT}>
                                    Persistent
                                </option>
                                <option value={SessionPersistenceType.EPHEMERAL}>Ephemeral</option>
                            </select>
                        </div>
                        <div className="flex items-end gap-3 pb-2">
                            <span className="p-[3px]">
                                <Checkbox
                                    id="promote_session_cookies"
                                    checked={props.state.promoteSessionCookies}
                                    onCheckedChange={(checked) =>
                                        props.onChange({
                                            ...props.state,
                                            promoteSessionCookies: checked === true,
                                        })
                                    }
                                />
                            </span>
                            <Label>Promote session cookies</Label>
                        </div>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                            <Label htmlFor="fetch_default_action">Fetch default action</Label>
                            <select
                                id="fetch_default_action"
                                className="w-full rounded-md border px-3 py-2"
                                value={props.state.fetchDefaultAction}
                                onChange={(event) =>
                                    props.onChange({
                                        ...props.state,
                                        fetchDefaultAction:
                                            event.target.value === UrlPatternsRuleAction.DENY
                                                ? UrlPatternsRuleAction.DENY
                                                : UrlPatternsRuleAction.ALLOW,
                                    })
                                }
                            >
                                <option value={UrlPatternsRuleAction.ALLOW}>Allow</option>
                                <option value={UrlPatternsRuleAction.DENY}>Deny</option>
                            </select>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="iframe_default_action">Iframe default action</Label>
                            <select
                                id="iframe_default_action"
                                className="w-full rounded-md border px-3 py-2"
                                value={props.state.iframeDefaultAction}
                                onChange={(event) =>
                                    props.onChange({
                                        ...props.state,
                                        iframeDefaultAction:
                                            event.target.value === UrlPatternsRuleAction.DENY
                                                ? UrlPatternsRuleAction.DENY
                                                : UrlPatternsRuleAction.ALLOW,
                                    })
                                }
                            >
                                <option value={UrlPatternsRuleAction.ALLOW}>Allow</option>
                                <option value={UrlPatternsRuleAction.DENY}>Deny</option>
                            </select>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="url_patterns_rules">Fetch URL pattern rules JSON</Label>
                        <textarea
                            id="url_patterns_rules"
                            className="min-h-24 w-full rounded-md border px-3 py-2 font-mono text-sm"
                            value={props.state.urlPatternsRulesJson}
                            onChange={(event) =>
                                props.onChange({
                                    ...props.state,
                                    urlPatternsRulesJson: event.target.value,
                                })
                            }
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="iframe_url_patterns">Iframe URL pattern rules JSON</Label>
                        <textarea
                            id="iframe_url_patterns"
                            className="min-h-24 w-full rounded-md border px-3 py-2 font-mono text-sm"
                            value={props.state.iframeUrlPatternsJson}
                            onChange={(event) =>
                                props.onChange({
                                    ...props.state,
                                    iframeUrlPatternsJson: event.target.value,
                                })
                            }
                        />
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
